import { create } from 'zustand';
import flatMap from 'lodash/flatMap';
import find from 'lodash/find';
import lodashGet from 'lodash/get';
import { PrismUI, builderDispatch, WebUI } from '../../_utils/ui';
import fetchImage from '../../_utils/fetch-image';
import handleRefusedChange from '../utils/handle-refused-change';
import IU from '../../_utils/img/img-utils';
import TemplateCache from '../templates/template-cache';
import TemplateStore from '../templates/template-store';
import Utils from '../utils/utils';
import objectToFormData from '../../_utils/object-to-form-data-util';
import PageSpreads from '../edit-area/page-spreads';
import PrismJSON from '../prism/prism-json-encoder';
import { getCurrentSection } from '../trays/tray-helpers';
import {
  isCalendarEventPhotoSlot,
  isOptionalSlot,
  isClonedSlot,
} from '../utils/special-slots';
import genericHelpers from '../../_utils/generic-helpers';
import { useTemplateSelectStore } from './useTemplateStore';
import { shouldKeepSlotSelectedDuringUpload } from './use3DEditingStore';
import {
  isCanvas,
  isCalendar,
  isMultiPageStandardProduct,
  isMultiPageCardProduct,
  isAlbum,
} from '../trays/tray-determiner';
import { autofillOnImageUploadNormal } from '../utils/autofill';
import DpiThreshold from '../utils/dpi-threshold';

const { urlMunge } = IU.imgHelpers;

const isOptionalBackground = (key) => key === 'optional-bg';

const resolveImageId = (image) => {
  if (typeof image === 'number') return image;
  if (image.imgId) return image.imgId;
  return image.id;
};

// A photo slot counts toward the total only if it is user-fillable — i.e. not
// the optional background slot and not a system-managed calendar date slot.
const isCountableSlot = (key) =>
  !isOptionalBackground(key) && !isCalendarEventPhotoSlot(key);

const RETRY_PERIOD = 1000;
const MAX_RETRIALS = 20;

// Operations that show/hide the PrismUI.MODAL_MESSAGE overlay while in flight.
// Mirrors `prismAction.notifyableOperations` in `utils/stores.js` — kept as a
// separate copy here (rather than imported) to avoid a circular import between
// this store and `utils/stores.js`, which itself still imports this store.
const NOTIFYABLE_OPERATIONS = [
  'autofill',
  'set_theme',
  'add_page',
  'delete_page',
];

// Applies a `{ pages, pageCount, currentPage }` response payload to the store,
// mirroring `prismAction.updatePages` in `utils/stores.js`. Duplicated (rather
// than shared) for the same circular-import reason as NOTIFYABLE_OPERATIONS —
// `prismAction.updatePages` stays put since it is still called directly by
// several live consumers outside this migration's scope.
function applyResponsePages(get, set, data) {
  const { pages, pageCount, currentPage } = data || {};
  if (!pages) return;

  get().updatePrismItem({ pages, pageCount });

  builderDispatch.dispatch({
    type: WebUI.PAGES_CHANGED,
    detail: 'usePrismItemStore action handler',
  });

  if (currentPage > -1) {
    const index = (window.activeView && window.activeView.pageIndex) || 0;
    builderDispatch.delayedDispatch({
      type: WebUI.DO_SHOW_SPREAD,
      spread: new PageSpreads(get().prismItem).find(index),
    });

    // delayedDispatch here because HIDE_CUSTOM_PAGE listeners (chiclets-base) call
    // handleItemClick which dispatches again — synchronous dispatch would throw
    // "Cannot dispatch in the middle of a dispatch".
    builderDispatch.delayedDispatch({
      type: WebUI.HIDE_CUSTOM_PAGE,
    });
  }
}

function postForm(operation, url, data, callback, get, set) {
  return sendRequest(
    operation,
    url,
    {
      method: 'POST',
      body: objectToFormData(data),
      credentials: 'same-origin',
    },
    callback,
    get,
    set,
  );
}

function postJSONBody(operation, url, data, callback, get, set) {
  return sendRequest(
    operation,
    url,
    {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    },
    callback,
    get,
    set,
  );
}

function sendRequest(operation, url, fetchOptions, callback, get, set) {
  const shouldNotify = NOTIFYABLE_OPERATIONS.includes(operation);

  if (shouldNotify) {
    builderDispatch.delayedDispatch({
      type: PrismUI.MODAL_MESSAGE,
    });
  }

  return fetch(url, fetchOptions).then(async (response) => {
    if (!response.ok) {
      // The overlay only hides on the success path below, so drop it before prompting - the
      // login dialog must not be asked over a full-screen spinner.
      if (shouldNotify) {
        builderDispatch.dispatch({
          type: PrismUI.MODAL_MESSAGE,
          action: 'hide',
        });
      }

      // A refused change is not a transport error: the customer needs to log in, or to be told
      // the project is not theirs. Throwing here left the 403 as an unhandled rejection.
      const refused = await handleRefusedChange(response, {
        onRetry: () =>
          sendRequest(operation, url, fetchOptions, callback, get, set),
      });

      if (refused) return;

      throw Error(response.statusText);
    }

    try {
      const data = await response.json();

      applyResponsePages(get, set, data);

      if (shouldNotify) {
        builderDispatch.delayedDispatch({
          type: PrismUI.MODAL_MESSAGE,
          action: 'hide',
        });
      }

      if (typeof callback === 'function') {
        callback(data);
      }
    } catch (error) {
      console.error(error);
    }
  });
}

// Upload keys already settled for the running upload batch. Kept outside state
// so progress subscribers don't re-render on every key; markUploadSettled
// starts a fresh set whenever the active batchId changes.
let settledUploads = { batchId: null, keys: new Set() };

// A file whose upload never reports back (its record lost, or never picked up
// by useUploadMonitor) would hold its batch open, and every later upload joins
// the open batch, so none would reach UPLOAD_DONE again. A batch with no file
// settling for this long is finished as it stands.
export const UPLOAD_BATCH_STALL_MS = 5 * 60 * 1000;
let uploadStallTimer = null;

const finishUploadBatch = (set, { totalFiles, batchId, sourceFiles }) => {
  clearTimeout(uploadStallTimer);
  uploadStallTimer = null;
  set({ imagesUploadProgress: { current: 0, totalFiles: 0 } });
  builderDispatch.dispatch({
    type: WebUI.UPLOAD_DONE,
    number_of_files: totalFiles,
    batch_id: batchId,
    source_files: sourceFiles,
    images_created: totalFiles,
  });
};

const watchUploadBatch = (set, get) => {
  clearTimeout(uploadStallTimer);
  uploadStallTimer = setTimeout(() => {
    uploadStallTimer = null;
    const progress = get().imagesUploadProgress;
    if (!progress.totalFiles) return;

    console.warn(
      `[upload] batch ${progress.batchId} stalled at ${settledUploads.keys.size} of ${progress.totalFiles} files; finishing it`,
    );
    finishUploadBatch(set, progress);
  }, UPLOAD_BATCH_STALL_MS);
};

const fetchImageList = async (prismItemId) => {
  const response = await fetch(`/m/i/${prismItemId}`, {
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`Failed to refresh images: HTTP ${response.status}`);
  }
  return response.json();
};

// Only the newest refreshImages() call writes imageList: an older response
// arriving last would drop images uploaded since (e.g. one UPLOAD_PROGRESS per
// file). A superseded call settles with the newest call's result instead.
let latestImagesRefresh = null;

export const usePrismItemStore = create((set, get) => ({
  prismItem: {
    id: '',
    data: null,
    currentPage: 0,
    pageCount: 0,
    activeSaves: {},
    imageList: [],
    loadActive: false,
    _fireSaves: {},
  },
  prismItemLoaded: false,
  // In-session snapshot of deleted fixed SVG text slots, keyed by
  // [pageIndex][templateId]. Stored as { id, deletedPlaceholderSvgUrl }[]
  // so the placeholder SVG can be restored without regeneration on return.
  deletedFixedSlots: {},
  saveDeletedFixedSlots: (pageIndex, templateId, slots) => {
    set((state) => ({
      deletedFixedSlots: {
        ...state.deletedFixedSlots,
        [pageIndex]: {
          ...(state.deletedFixedSlots[pageIndex] || {}),
          [templateId]: slots,
        },
      },
    }));
  },
  getDeletedFixedSlots: (pageIndex, templateId) => {
    return get().deletedFixedSlots[pageIndex]?.[templateId] || [];
  },
  imagesUploadProgress: {
    current: 0,
    totalFiles: 0,
  },
  failedImagesUploaded: [],
  // Starts an upload batch, or folds these files into the one already running
  // so overlapping uploads end in a single UPLOAD_DONE. Returns the batchId
  // every file's upload must settle against.
  beginUploadBatch: ({ totalFiles, batchId, sourceFiles }) => {
    const active = get().imagesUploadProgress;

    if (active.totalFiles > 0 && active.batchId) {
      set({
        imagesUploadProgress: {
          ...active,
          totalFiles: active.totalFiles + totalFiles,
          sourceFiles: (active.sourceFiles || 0) + sourceFiles,
        },
      });
      watchUploadBatch(set, get);
      return active.batchId;
    }

    settledUploads = { batchId, keys: new Set() };
    set({
      imagesUploadProgress: { current: 1, totalFiles, batchId, sourceFiles },
    });
    watchUploadBatch(set, get);
    return batchId;
  },
  // Records that one file of the batch has finished, successfully or not.
  // Keyed so a file reported by more than one path (or replayed by the upload
  // monitor on remount) counts once; settles for another batch are ignored.
  markUploadSettled: ({ batchId, key }) => {
    const {
      totalFiles,
      batchId: activeBatchId,
      sourceFiles,
    } = get().imagesUploadProgress;

    if (!totalFiles || !batchId || batchId !== activeBatchId || key == null) {
      return;
    }

    if (settledUploads.batchId !== batchId) {
      settledUploads = { batchId, keys: new Set() };
    }
    if (settledUploads.keys.has(key)) return;
    settledUploads.keys.add(key);

    const settled = settledUploads.keys.size;

    if (settled >= totalFiles) {
      finishUploadBatch(set, { totalFiles, batchId, sourceFiles });
    } else {
      watchUploadBatch(set, get);
      set({
        imagesUploadProgress: {
          current: settled + 1,
          totalFiles,
          batchId,
          sourceFiles,
        },
      });
    }
  },
  isLayflat: () => {
    return flatMap(
      get().prismItem.pages,
      (page) => (page.template || {}).tags || [],
    ).includes('layflat');
  },
  minPagesReached: () => {
    return (
      get().prismItem.product &&
      get().prismItem.pageCount <= get().prismItem.product.minPages
    );
  },
  maxPagesReached: () => {
    return (
      get().prismItem.product &&
      get().prismItem.pageCount >= get().prismItem.product.maxPages
    );
  },
  // Migrated from `PrismInfo` in `utils/stores.js` (DEBT-028). `PrismInfo.prismItem`,
  // `.pages`, and `.sections` are not migrated as selectors of their own — they were
  // plain passthroughs to `prismItem`, `prismItem.pages`, and `prismItem.product.sections`,
  // which are already directly accessible on this store's state. `PrismInfo.canAddPages`
  // is likewise not migrated — it was a passthrough to `maxPagesReached`, above, which
  // already covers that need.
  hasDoubleSpread: () => {
    const pages = get().prismItem.pages;
    for (const key in pages) {
      if (typeof pages[key]['page-pair'] === 'number') return true;
    }

    return false;
  },
  canReorder: () => {
    return get().prismItem.product.sections.some((section) => section.max > 1);
  },
  pageIsLeftSide: (pageIndex) => {
    // `page-is` should be the authoritative side of a page within its spread,
    // provided by web-prism. It defaults to 'right' when absent, matching the
    // rest of the builder (navigate-to-spread, preview, thumbnails).
    const page = get().prismItem.pages[pageIndex];
    return (lodashGet(page, 'page-is') || 'right') === 'left';
  },
  selectTheme: () => {},
  listPages: () => {
    const state = get();
    if (!state || !state.prismItem) return [];
    return Object.keys(state.prismItem.pages || {}).map(
      (p) => state.prismItem.pages[p],
    );
  },
  totalNumberPhotoSlots: async () => {
    try {
      const state = get();
      if (!state || !state.prismItem || !state.prismItem.pages) return 0;

      const pages = state.prismItem.pageCount;
      if (!pages) return 0;

      const pageTemplates = await Promise.all(
        Array.from({ length: pages }, (_, i) => state.pageWithTemplate(i)),
      );

      const total = pageTemplates.reduce((total, { template }) => {
        if (!template?.photo_slots) return total;

        const validSlots = Object.entries(template.photo_slots).filter(
          ([key, slot]) => {
            const isValidSlotObject = slot && typeof slot === 'object';
            return isValidSlotObject && isCountableSlot(key);
          },
        );

        return total + validSlots.length;
      }, 0);
      return total;
    } catch (error) {
      console.error('Error calculating total slots:', error);
      return 0;
    }
  },
  totalNumberStackablePhotoSlots: async () => {
    try {
      const state = get();
      if (!state || !state.prismItem || !state.prismItem.pages) return 0;

      const pages = state.prismItem.pages;

      if (!pages || pages === null || pages === undefined) {
        return 0;
      }

      return Object.values(pages)
        .flatMap((page) => page?.slots || [])
        .filter(
          (slot) =>
            slot?.type === 'stackable' && slot?.content_type === 'image',
        ).length;
    } catch (error) {
      console.error('Error calculating total slots:', error);
      return 0;
    }
  },
  onlyHasOnePhotoSlot: async () => {
    const total = await get().totalNumberPhotoSlots();
    return total === 1;
  },
  fireSave: (view, callback = null) => {
    view = view || activeView;

    // Resolve the page's current index from its stable id rather than the
    // view's cached pageIndex, which can be stale if pages were reindexed (e.g.
    // a page inserted before it) between the edit and this save firing.
    const pages = get().prismItem.pages;
    const matchedIndex =
      view.pageId != null
        ? Object.keys(pages).find(
            (i) => pages[i] && pages[i].id === view.pageId,
          )
        : undefined;
    const pageIndex =
      matchedIndex !== undefined ? Number(matchedIndex) : view.pageIndex;

    // Nothing to persist: either a blank facing page (single-sided spreads have
    // no id or index) or a page that was deleted (its stable id is no longer in
    // `pages`). Writing would land on `pages[undefined]` or a stale index and
    // desync `pages` from `pageCount`, so bail — but still run the callback so
    // chained saves (e.g. fireSave(otherView, () => fireSave(activeView))) go on.
    const pageWasRemoved = view.pageId != null && matchedIndex === undefined;
    if (pageWasRemoved || pageIndex == null) {
      if (callback) callback();
      return;
    }

    const data = view.getData(true);

    get().prismItem.pages[pageIndex] = {
      ...get().prismItem.pages[pageIndex],
      data,
    };

    get().prismItem._fireSaves = get().prismItem._fireSaves || {};
    get().prismItem._fireSaves[pageIndex] =
      get().prismItem._fireSaves[pageIndex] || [];
    get().prismItem._fireSaves[pageIndex].unshift(view);

    if (get().prismItem._fireSaves[pageIndex].length > 1) {
      return;
    }

    get()
      .savePage(pageIndex, data)
      .then(() => {
        const nextView = get().prismItem._fireSaves[pageIndex].shift();
        const thereWasMore = get().prismItem._fireSaves[pageIndex].length > 0;

        get().prismItem._fireSaves[pageIndex] = [];

        if (thereWasMore) {
          get().fireSave(nextView);
        } else {
          if (callback) callback();
        }
      });
  },
  setDayEventsSlots: async () => {
    const pagesWithDailyTextSlots = [];

    for (let i = 0; i < get().prismItem.pageCount; i++) {
      const { page, template } = await get().pageWithTemplate(i);
      const pageWithDailyTextSlots = {};

      for (const slotId in template.text_slots) {
        if (/text-\d\d\d\d-\d\d-\d\d/gi.exec(slotId)) {
          let slot;

          for (let x = 0; x < page.slots.length; x++) {
            if (page.slots[x].id === slotId) {
              slot = page.slots[x];
              break;
            }
          }

          pageWithDailyTextSlots[slotId] = !!(slot && slot.image);
        }
      }

      if (Object.keys(pageWithDailyTextSlots).length > 0) {
        pagesWithDailyTextSlots.push(pageWithDailyTextSlots);
      }
    }

    return pagesWithDailyTextSlots;
  },
  getSelectedAlbumImages: async () => {
    const { id: prismItemId, sec: prismItemSec } = get().prismItem ?? {};

    if (!prismItemId) return;

    if (!localStorage.selected_albums) return;

    const importUrl = `/project_edit/import/${wkData.id}`;
    const importForm = new FormData();
    const albumData = JSON.parse(localStorage.selected_albums);
    let imageIdArray = [];
    for (const albumId in albumData) {
      imageIdArray = imageIdArray.concat(albumData[albumId].photos);
    }

    importForm.append('image_ids', imageIdArray.join(','));

    const importResponse = await fetch(importUrl, {
      method: 'POST',
      credentials: 'same-origin',
      body: importForm,
    }).then((response) => response.json());

    if (
      typeof importResponse === 'undefined' ||
      typeof importResponse.error !== 'undefined'
    )
      return;

    const addUrl = `/edt/img/add/${prismItemSec}/${prismItemId.split('o')[1]}?json=1`;
    const importValues = importResponse.response;
    let promiseArray = [];
    let uploadCount = Object.keys(importValues).length;

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_BEGUN,
      number_of_files: uploadCount,
    });

    try {
      promiseArray = Object.values(importValues).map(async (url) => {
        const addForm = new FormData();
        addForm.append('source', `${location.origin}${App.order.basket_path}`);

        const imageData = await fetch(url, { credentials: 'same-origin' }).then(
          (response) => response.blob(),
        );
        addForm.append('file', imageData);

        return fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          body: addForm,
        }).then((data) => {
          builderDispatch.dispatch({
            type: WebUI.UPLOAD_PROGRESS,
            data,
            number_of_files: --uploadCount,
          });
          return data;
        });
      });
    } catch (error) {
      console.error('Upload from album failed: ', error);
    }

    if (promiseArray.length > 0) {
      return Promise.all(promiseArray);
    }
  },
  refreshImages: () => {
    const prismItemId = get().prismItem.id;

    if (!prismItemId) return Promise.resolve();

    const refresh = fetchImageList(prismItemId).then(
      (data) => {
        if (refresh !== latestImagesRefresh) return latestImagesRefresh;
        if (get().prismItem.id !== prismItemId) {
          throw new Error('Failed to refresh images: the item changed');
        }

        set(({ prismItem }) => ({
          prismItem: { ...prismItem, imageList: data },
        }));
        return data;
      },
      (error) => {
        if (refresh !== latestImagesRefresh) return latestImagesRefresh;
        throw error;
      },
    );
    latestImagesRefresh = refresh;
    return refresh;
  },
  hasSingleImageSlot: () => {
    if (!window.activeView) {
      return false;
    }

    const imageSlots = window.activeView.slots.filter((slot) => slot.isImage);

    return imageSlots.length === 1;
  },
  autoFillImageUponUpload: () => {
    // register-3d-photo-upload-autofill.js already places the upload for this
    // case — this legacy autofill must not also run, since it reloads the
    // page/template from the server and tears down the 3D selection and its
    // controls out from under the user mid-upload. See "Keeping a 3D slot
    // selected through a photo upload" in common/AGENTS.md.
    if (shouldKeepSlotSelectedDuringUpload()) {
      return;
    }

    const prismItem = get().prismItem;
    const allImages = prismItem?.imageList;
    const hasImages = allImages && allImages.length > 0;

    const autoFillCriteria =
      hasImages && get().hasSingleImageSlot() && !isMultiPageStandardProduct();

    if (autoFillCriteria) {
      // Autofill only adds unused photos to empty slots, so with the one slot
      // already filled it changes nothing, but its response still re-shows the
      // spread, deselecting a slot the user just picked (#14276).
      const imageSlot = window.activeView.slots.find((slot) => slot.isImage);
      if (imageSlot.img) return;

      const imageList =
        isMultiPageCardProduct() || isCalendar() ? [allImages[0]] : allImages;

      autofillOnImageUploadNormal(imageList);
    } else {
      // Autofill criteria not met, do-show-spread so that photos tray is displayed for user.
      builderDispatch.delayedDispatch({ type: WebUI.DO_SHOW_SPREAD });
    }
  },
  addNewImagesToItem: (imagesList) => {
    let changed = false;

    for (let i = 0; i < imagesList.length; i++) {
      const slotId = activeView.findFirstAvailableImageSlot();

      // break if no slot is available
      if (!slotId) break;

      // set the image in the available slot
      const img = fetchImage(urlMunge(imagesList[i].url), 'use-credentials');
      activeView.setImageById(slotId, img, {
        image: imagesList[i].id,
      });
      changed = true;
    }

    // If a change happened, save
    if (changed) activeView.do_save();
  },
  checkSlots: () => {
    let filledImageSlot = false;
    let emptyImageSlot = false;
    let unchangedDefaultText = false;
    let spineTextSlotPresent = false;
    let spineTextPresent = false;
    let lowDpiImage = false;
    let emptyPage = false;
    const pagePromises = Object.entries(get().prismItem.pages)
      .filter(([key, _]) => isFinite(key))
      .map(([_, page]) => evaluatePage(page));

    // when ALL the pagePromises are resolved
    return Promise.all(pagePromises).then(() => ({
      filledImageSlot,
      emptyImageSlot,
      unchangedDefaultText,
      spineTextSlotPresent,
      spineTextPresent,
      lowDpiImage,
      emptyPage,
    }));

    function isSkippableSlot(slot) {
      return [isCalendarEventPhotoSlot, isOptionalSlot, isClonedSlot].some(
        (check) => check(slot.id),
      );
    }

    function canPageBeBlank(tags) {
      if (!tags) return false;
      return tags.includes('is-blank') && tags.includes('can-be-blank');
    }

    function evaluatePage(page) {
      if (
        [
          filledImageSlot,
          emptyImageSlot,
          unchangedDefaultText,
          spineTextSlotPresent,
          spineTextPresent,
          lowDpiImage,
          emptyPage,
        ].every(Boolean)
      ) {
        // there's no need checking anything if we already know all items are true
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        TemplateCache.get(page.templateID, (template) => {
          // Slots are not created until a page in the builder is viewed
          // If product is a book, calendar or card set emptyPage true so user is prompted
          // for other products check tags for is-blank and can-be-blank.
          // Only set emptyPage true if page is not allowed to be blank
          if (!emptyPage && page.slots.length < 1) {
            const isMultiPage =
              isAlbum() || isCalendar() || isMultiPageCardProduct();
            if (isMultiPage) {
              emptyPage = true;
            } else {
              emptyPage = canPageBeBlank(template?.tags) ? false : true;
            }
          }

          // Check all slots (predefined template slots as well as user added image slots) to see if
          // any of them have been filled
          if (!filledImageSlot || !lowDpiImage) {
            for (const slot of page.slots) {
              // Some special slots can be skipped as we don't want to include them in the checks, we do need to check

              let isFrontOrBack = false;
              if (slot.id === 'optional-front' || slot.id === 'optional-back') {
                isFrontOrBack = true;
              }

              if (isSkippableSlot(slot) && isFrontOrBack == false) {
                continue;
              }

              if (!slot.data.layout) {
                const templateCoord =
                  get().listPages()?.[page?.index]?.template?.photo_slots?.[
                    slot?.id
                  ]?.['pixel-coords'];

                if (!templateCoord) {
                  console.warn(
                    'Template layout is missing for page index:',
                    page?.index,
                    'or slot id:',
                    slot?.id,
                  );
                }

                const [x, y, w, h] = templateCoord ?? [0, 0, 1, 1];
                slot.data.layout = { x, y, w, h };
              }

              if (slot.image && slot.data.layout) {
                filledImageSlot = true;

                const {
                  layout: { w, h },
                  angle,
                  imgId,
                } = slot.data;
                const photoData = get().prismItem.imageList.find(
                  (image) => image.id === Number(imgId),
                );
                if (!photoData) return;

                if (lowDpiImage === false) {
                  if (slot.data.lowDpi) {
                    lowDpiImage = true;
                  } else {
                    const dpi = genericHelpers.getImageOutputDpiWithSlotData(
                      slot.data,
                      photoData,
                      slot.type === 'stackable',
                    );
                    lowDpiImage = slot.data.lowDpi = new DpiThreshold(
                      dpi,
                    ).isTooLow;
                  }
                }
              }

              // Only check predefined slots to see if any of them are empty, stackable slots are
              // allowed to be empty.
              if (template.photo_slots[slot.id] && !slot.image) {
                emptyImageSlot = true;
              }
            }
          }

          // Only check predefined text slots to see if any of them still contain their default text
          // value
          if (
            !unchangedDefaultText &&
            Object.keys(template.text_slots).length
          ) {
            for (const slotId in template.text_slots) {
              const slot = find(page.slots, { id: slotId })
                ? find(page.slots, { id: slotId })
                : find(page.slots, { id: 'override-text-' + slotId });

              if (slot && isSkippableSlot(slot)) {
                return;
              }

              const templateSlot = template.text_slots[slotId];
              const slotsExist = slot && templateSlot ? true : false;

              if (
                slotsExist &&
                templateSlot.default.localeCompare(
                  slot.data.string,
                  undefined,
                  { sensitivity: 'base' },
                ) === 0
              ) {
                unchangedDefaultText = true;
              }

              break;
            }
          }
          if (!spineTextSlotPresent && 'spine-text' in template.text_slots) {
            spineTextSlotPresent = true;

            const pages = Object.values(prismItem.pages);

            spineTextPresent = pages.some(
              (page) =>
                Array.isArray(page.slots) &&
                page.slots.some(
                  (slot) =>
                    slot.id === 'spine-text' && slot.data?.string?.length > 0,
                ),
            );
          }

          resolve();
        });
      });
    }
  },

  populateItemsImagesAfterUpload: (imageData) => {
    if (imageData.length === 0) return;

    const newImageIds = imageData.map((i) => i.id);
    const newImages = get().prismItem.imageList.filter(
      (i) => newImageIds.indexOf(i.id) >= 0,
    );

    get().addNewImagesToItem(newImages);
  },
  // Add images from the list to empty slots in a collage project
  addNewImagesToItem: (imagesList) => {
    let changed = false;

    for (let i = 0; i < imagesList.length; i++) {
      const slotId = activeView.findFirstAvailableImageSlot();

      // break if no slot is available
      if (!slotId) break;

      // set the image in the available slot
      const img = fetchImage(urlMunge(imagesList[i].url), 'use-credentials');
      activeView.setImageById(slotId, img, {
        image: imagesList[i].id,
      });
      changed = true;
    }

    // If a change happened, save
    if (changed) activeView.do_save();
  },
  findImage: (ref) => {
    for (let i = 0; i < get().prismItem.imageList.length; i++) {
      const image = get().prismItem.imageList[i];

      if (ref.image == image.id) {
        let { filter } = ref.data;
        if (!filter) filter = 'colour';
        ref.data.url = image.urls[filter];
      }
    }
    return ref;
  },
  usedImages: () => {
    const used = [];
    for (let i = 0; i < get().prismItem.pageCount; i++) {
      if (get().prismItem.pages[i] && get().prismItem.pages[i].slots) {
        const pg = get().prismItem.pages[i];
        for (let j = 0; j < get().prismItem.pages[i].slots.length; j++) {
          const { image } = pg.slots[j];
          if (image && used.indexOf(image) === -1) {
            used.push(image);
          }
        }
      }
    }
    return used;
  },
  usedImagesOnPage: (pageIndex) => {
    const pg = get().prismItem.pages[pageIndex];
    if (!pg || !pg.slots) return [];
    const seen = new Set();
    const used = [];
    for (const slot of pg.slots) {
      if (slot.image && !seen.has(slot.image)) {
        seen.add(slot.image);
        used.push(slot.image);
      }
    }
    return used;
  },
  pageWithTemplate: (index) => {
    return new Promise((resolve, reject) => {
      get().getPage(index, (page) => {
        TemplateCache.get(
          page.templateID,
          (template) => {
            resolve({ page, template });
          },
          () => {
            reject(new Error(`Failed to load template ${page.templateID}`));
          },
        );
      });
    });
  },
  getPage: (index, callback) => {
    callback(get().prismItem.pages[index]);
  },
  savePage: (index, data, options = {}) => {
    if (typeof index === 'undefined') {
      return Promise.resolve();
    }

    const saveID = `save-${parseInt(
      Math.random() * 1000,
      10,
    )}-${index}-${new Date().getTime()}`;

    $(window).trigger('prism-page-save-start', { index, saveID });

    if (builderDispatch) {
      builderDispatch.delayedDispatch({
        type: PrismUI.SAVE_START,
        saveID,
        pageIndex: index,
        prismItem: get().prismItem,
      });
    }

    if (wkData && wkData.values) {
      const overlays = {};

      for (const { id, name } of wkData.vars) {
        if (wkData.values[id]) {
          overlays[name] = wkData.values[id];
        }
      }

      data.overlays = overlays;
    }

    const formData = new FormData();
    formData.append('data', PrismJSON.encode(data));

    return fetch(Utils.pageURL(get().prismItem, index), {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        const { doNotSave = false } = options;

        if (!doNotSave) {
          const pages = get().prismItem.pages;
          pages[index] = data;
          set(({ prismItem }) => ({
            prismItem: { ...prismItem, pages: pages },
          }));
        }

        $(window).trigger('prism-page-save-done', { index, saveID });

        if (builderDispatch) {
          builderDispatch.delayedDispatch({
            type: PrismUI.SAVE_DONE,
            saveID,
            pageIndex: index,
            prismItem: get().prismItem,
          });
        }
      })
      .catch((e) => console.error(e));
  },
  // Given a number of files to fit, will select and apply the correct template for the current page.
  selectTemplateToFitImages: (nOfFiles, orientationTag) => {
    if (nOfFiles < 1) return;

    const activeSection = getCurrentSection();
    if (!activeSection) return; // Return if we are unable to select the correct section for getting the right templates

    const template = TemplateStore.getTemplateByPhotoSlots(
      nOfFiles,
      activeSection,
      orientationTag,
    );

    // Fire an event to apply the chosen template
    useTemplateSelectStore
      .getState()
      .updateTemplateSelectStore({ templateID: template.id });
  },
  updatePrismLoaded: () => set(() => ({ prismItemLoaded: true })),
  updateId: (id) =>
    set(({ prismItem }) => ({ prismItem: { ...prismItem, id: id } })),
  updateLoadActive: (loadActive) =>
    set(({ prismItem }) => ({
      prismItem: { ...prismItem, loadActive: loadActive },
    })),
  updateImageList: (imageList) => {
    set(({ prismItem }) => ({
      prismItem: { ...prismItem, imageList: imageList },
    }));
  },
  updatePrismItem: (props) =>
    set(({ prismItem }) => ({ prismItem: { ...prismItem, ...props } })),
  imagesEmpty: () => (get().prismItem.imageList || []).length === 0,
  imagesAny: () => !get().imagesEmpty(),
  listImages: () => {
    const { prismItem } = get();
    if (!prismItem || !prismItem.imageList) return [];

    const usedImageIds = get()
      .usedImages()
      .reduce((memo, usedImageId) => {
        memo[usedImageId] = true;
        return memo;
      }, {});

    return prismItem.imageList.map((image) =>
      Object.assign(image, { used: usedImageIds[image.id] === true }),
    );
  },
  removeImage: (image) => {
    const id = resolveImageId(image);
    const { prismItem } = get();

    return fetch(`/edt/img/del/${prismItem.sec}/${prismItem.id}/${id}`, {
      method: 'POST',
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        // removing image reference from WK
        fetch(`/image_references/${prismItem.id}/${id}`, {
          method: 'DELETE',
          credentials: 'same-origin',
        }).catch((e) => console.error(e));

        return response;
      })
      .then(() => {
        const newList = get()
          .listImages()
          .filter((image) => resolveImageId(image) !== id);

        get().updateImageList(newList);
      })
      .catch((e) => console.error(e));
  },
  // Migrated from `prismAction` in `utils/stores.js` (DEBT-028). The remaining
  // `prismAction` methods (`post`, `postJSON`, `updatePages`, `deleteImage`, `clear`)
  // stay in `utils/stores.js` — they are still called directly by live consumers
  // outside this migration's scope.
  autofill: (payload, callback) => {
    return postJSONBody(
      'autofill',
      `/m/a/${get().prismItem.id}/autofill`,
      payload,
      callback,
      get,
      set,
    );
  },
  autoclear: (payload, callback) => {
    return postJSONBody(
      'autoclear',
      `/m/a/${get().prismItem.id}/autoclear`,
      payload,
      callback,
      get,
      set,
    );
  },
  set_theme: (theme, callback) => {
    return postForm(
      'write',
      `/project_edit/write/${get().prismItem.id}`,
      { theme },
      () => {
        postForm(
          'set_theme',
          `/m/a/${get().prismItem.id}/set_theme`,
          { theme, index: get().prismItem.currentPage },
          (data) => {
            set(({ prismItem }) => ({
              prismItem: {
                ...prismItem,
                theme: data.theme,
                design_code: data.design_code,
              },
            }));

            if (typeof callback === 'function') {
              callback(data);
            }

            builderDispatch.dispatch({
              type: PrismUI.THEME_CHANGED,
              theme,
            });
          },
          get,
          set,
        );
      },
      get,
      set,
    );
  },
  add_page: (position, callback, index) => {
    if (typeof index === 'undefined') {
      const isLeftSide = get().pageIsLeftSide(activeView.pageIndex);

      position =
        {
          left: 'before',
          right: 'after',
        }[position] || (isLeftSide ? 'before' : 'after');

      index = (({ pageIndex }) => {
        if (get().isLayflat() || !get().hasDoubleSpread()) {
          return pageIndex;
        }

        if (position === 'before') {
          pageIndex -= isLeftSide ? 0 : 1;
        }

        if (position === 'after') {
          pageIndex += isLeftSide ? 1 : 0;
        }

        return pageIndex;
      })(activeView);
    }

    return postForm(
      'add_page',
      `/m/a/${get().prismItem.id}/add_pages`,
      { index, position },
      callback,
      get,
      set,
    );
  },
  delete_page: (callback, pageIndex = null) => {
    const index = (({ pageIndex: pageIndexActiveView }) => {
      pageIndex = pageIndex ?? pageIndexActiveView;
      if (get().isLayflat() || !get().hasDoubleSpread()) {
        return pageIndex;
      }

      const isLeftSide = get().pageIsLeftSide(pageIndex);

      return pageIndex - (isLeftSide ? 0 : 1);
    })(activeView);

    return postForm(
      'delete_page',
      `/m/a/${get().prismItem.id}/delete_pages`,
      { index },
      callback,
      get,
      set,
    );
  },
  save_wkdata: (callback) => {
    return postForm(
      'save_wkdata',
      wkData.write_url,
      { values: wkData.values },
      callback,
      get,
      set,
    );
  },
  // Migrated from `loadPrismItem()` in `utils/stores.js` (DEBT-028).
  load: (vars, options = {}, skipAddToStack = false) => {
    const { initialPage } = options;
    builderDispatch.dispatch({
      type: PrismUI.ITEM_COPYING,
    });

    if (!vars.hasOwnProperty('id')) {
      return Promise.reject();
    }

    const dateIdentifier = Date.now();
    if (skipAddToStack && activeView) {
      activeView.historyStack.skipStep(dateIdentifier);
    }

    Utils.load();
    get().updateId(vars.id);
    get().updateLoadActive(true);

    return new Promise((resolve, reject) => {
      perform({
        url: Utils.dataURL(vars),
        resolve,
        reject,
      });
    }).finally(() => {
      builderDispatch.dispatch({
        type: PrismUI.ITEM_COPYING_FINISHED,
      });
    });

    function perform({ url, resolve, reject, attempt = 0 }) {
      fetch(url, {
        credentials: 'same-origin',
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }

          throw new Error(response.statusText);
        })
        .then((data) => {
          if (data.copy && data.copy.status) {
            switch (data.copy.status) {
              case 'queued':
              case 'working':
                builderDispatch.dispatch({
                  type: PrismUI.ITEM_COPYING,
                  data: data.copy,
                });

                if (attempt < MAX_RETRIALS) {
                  setTimeout(() => {
                    perform({
                      url,
                      resolve,
                      reject,
                      attempt: ++attempt,
                    });
                  }, RETRY_PERIOD);
                } else {
                  reject('Transformation timed out');
                }
                break;
              case 'completed':
              case undefined: // This is the default returned value upon completion!
                resolve();
                break;
              default:
                reject('Transformation failure');
                break;
            }
          } else {
            if (initialPage) data.currentPage = initialPage;
            builderDispatch.dispatch({
              type: PrismUI.DO_ITEM_LOAD,
              data,
              dateIdentifier,
            });

            resolve();
          }
        })
        .catch((error) => {
          get().updateLoadActive(false);
          reject(error.message);
        });
    }
  },
}));
